import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcOrdersV1BatchOrderQueryServiceBatchOrderGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { getReadonlyListItemString } from '@interxfi/app-components/components/readonly-list/util';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DEFAULT_WRAPPING_LAYOUT_COLUMN, LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container';
import { WrappingLayoutContainer } from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcOrdersV1BatchOrderState } from '@interxfi/types';
import { TableSectionHeader } from '../../../../../../../components/table/table-section-header';
import { BatchOrderAllocationsTable } from '../../../../../../../features/order/batch/allocation/batch-order-allocations-table';
import { BatchOrderEntityMenu } from '../../../../../../../features/order/batch/batch-order-entity-menu';
import { BatchOrderEventSummary } from '../../../../../../../features/order/batch/batch-order-event';
import { getBatchOrderStateListItems } from '../../../../../../../features/order/batch/batch-order-list-items';
import { getBatchOrderDetailPath } from '../../../../../../../features/order/batch/batch-order-routing';
import { BatchOrderStatusPill } from '../../../../../../../features/order/batch/batch-order-status-pill';
import { BatchOrderSummaryBar } from '../../../../../../../features/order/batch/batch-order-summary-bar';
import { OrderSidePill } from '../../../../../../../features/order/order-side-pill';
import { ProgramLogomark } from '../../../../../../../features/userauth/program/logomark/program-logomark';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';
import { type RollupViewContextType, useRollupViewContext } from '../../../../../../../util/view-context';

export const Route = createFileRoute('/_app/_clearing-core/order/equity/batch/_batch/$batchOrderId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `equities-batch-order-${params.batchOrderId}`,
        label: 'Batch Order',
        to: getBatchOrderDetailPath(params.batchOrderId),
        suffix: (
          <CopyButton
            content={params.batchOrderId}
            label="Copy Batch Order ID"
            tooltip={{ copy: params.batchOrderId, copied: 'Copied Batch Order ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Batch Order'),
  component: BatchOrderDetail,
});

function getListItems(data: IxcOrdersV1BatchOrderState | undefined, viewContext: RollupViewContextType) {
  const listItems = getBatchOrderStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [
          listItems.data.asset?.summary || listItems.data.instruction.symbol,
          ...(listItems.data.instruction.amount && ['quantity', 'notional'].includes(listItems.data.instruction.amount['!type'])
            ? [listItems.data.instruction.amount.value.value]
            : [getReadonlyListItemString('amount', !data ? <Skeleton charCount={6} type="line" /> : 'Amount', undefined)]),
          listItems.data.instruction.typeName,
          ...(listItems.data.instruction.limitPrice ? [listItems.data.instruction.limitPrice] : []),
          listItems.data.instruction.timeInForce,
          listItems.data.quotePrice,
          listItems.data.holdBufferPercent,
          listItems.data.execution.market,
          listItems.data.fillTotals.filledQuantity,
          listItems.data.fillTotals.filledAmount,
          listItems.data.fillTotals.grossAmount,
          listItems.data.fillTotals.netAmount,
        ],
      },
      ...(listItems.data.fillTotals.middleMoneyAsKeyValuePairs.length
        ? [
            {
              id: 'middleMoney',
              title: 'Middle Money',
              items: listItems.data.fillTotals.middleMoneyAsKeyValuePairs,
            },
          ]
        : []),
      ...(data?.data?.extension
        ? [
            {
              id: 'extension',
              items: [
                listItems.data.extension.solicited,
                listItems.data.extension.trailer,
                ...(listItems.data.extension.commission
                  ? [
                      listItems.data.extension.commission.value.typeName,
                      ...(listItems.data.extension.commission['!type'] === 'fixed' ? [listItems.data.extension.commission.value.amount] : []),
                      ...(listItems.data.extension.commission['!type'] === 'simpleRate'
                        ? [
                            listItems.data.extension.commission.value.rate,
                            listItems.data.extension.commission.value.basis,
                            ...(listItems.data.extension.commission.value.minimum ? [listItems.data.extension.commission.value.minimum] : []),
                            ...(listItems.data.extension.commission.value.maximum ? [listItems.data.extension.commission.value.maximum] : []),
                          ]
                        : []),
                    ]
                  : [getReadonlyListItemString('commission', 'Commission', undefined)]),
              ],
            },
          ]
        : []),
      {
        id: 'counts',
        items: [
          listItems.data.counts.total,
          listItems.data.counts.ready,
          listItems.data.counts.failed,
          listItems.data.counts.created,
          listItems.data.counts.skipped,
          listItems.data.counts.distributed,
        ],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [
      listItems.data.execution.settlementDate,
      listItems.data.execution.tradeDate,
      listItems.data.execution.id,
      listItems.batchOrderId,
      listItems.accountId,
      ...(viewContext !== 'rollup-program' ? [listItems.programId] : []),
      listItems.data.orderId,
    ] as const satisfies ReadonlyListItem[],
  };
}

function AllocationsTableHeader(props: CustomTableHeaderProps) {
  return <TableSectionHeader {...props} title="Batch Allocations" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load batch order', 'view this batch order');

function BatchOrderDetail() {
  const { batchOrderId } = Route.useParams();
  const request = { batchOrderId } as const;
  const { data, error, isPending } = useIxcOrdersV1BatchOrderQueryServiceBatchOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const viewContext = useRollupViewContext();
  const listItems = useMemo(() => getListItems(data?.batchOrder, viewContext.kind), [data?.batchOrder, viewContext.kind]);
  const { activeTenantContext } = useActiveRealmAPIContext();

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            {activeTenantContext !== TenantContext.Program ? (
              <ProgramLogomark programId={data?.batchOrder?.programId} shouldLoadLogoMark size="large" withTooltip />
            ) : null}

            <BatchOrderStatusPill isPending={showPending} size="large" status={data?.batchOrder?.status} />

            <OrderSidePill isPending={showPending} side={data?.batchOrder?.data?.instruction?.side} size="large" />
          </HorizontalStack>

          <BatchOrderEntityMenu batchOrderId={batchOrderId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <BatchOrderSummaryBar batchOrderId={batchOrderId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <BatchOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <BatchOrderAllocationsTable batchOrderId={batchOrderId} HeaderActions={AllocationsTableHeader} />
      </PageLayout.Section>
    </PageLayout>
  );
}
